import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [account, setAccount] = useState(null);
	const [accessToken, setAccessToken] = useState(null);
	const [tokenCache, setTokenCache] = useState(null);
	const [roles, setRoles] = useState([]);
	const navigate = useNavigate();
	const location = useLocation();
	const sessionCheckIntervalRef = useRef(null);

	// Check if auth bypass is enabled for local testing
	const bypassAuth = process.env.REACT_APP_BYPASS_AUTH === 'true';

	// 5MB limit helper for fetch responses
	const FIVE_MB = 5 * 1024 * 1024;
	const safeFetchJson = async (fetchResponse) => {
		const contentLength = fetchResponse.headers.get('content-length');
		if (contentLength && parseInt(contentLength, 10) > FIVE_MB) {
			throw new Error('Response payload exceeds 5MB limit');
		}
		const buffer = await fetchResponse.arrayBuffer();
		if (buffer.byteLength > FIVE_MB) {
			throw new Error('Response payload exceeds 5MB limit');
		}
		return JSON.parse(new TextDecoder().decode(buffer));
	};

	// Session check function with Edge compatibility
	const checkSessionStatus = async () => {
		try {
			const fetchResponse = await fetch('/auth/session-check', {
				credentials: 'include',
				headers: {
					'Cache-Control': 'no-cache',
					'Pragma': 'no-cache'
				}
			});
			const data = await safeFetchJson(fetchResponse);
			if (!fetchResponse.ok) {
				const err = new Error(`Session check failed with status ${fetchResponse.status}`);
				err.response = { status: fetchResponse.status, data };
				throw err;
			}
			const response = { data, status: fetchResponse.status };

			if (!response.data.authenticated) {
				// console.log('Session expired, redirecting to login...');
				// Clear session data and redirect
				sessionStorage.removeItem('authData');
				setIsAuthenticated(false);
				setAccount(null);
				setAccessToken(null);
				window.location.href = response.data.redirect || '/auth/login';
			}
			// If authenticated, do nothing - continue normal operation
		} catch (error) {
			// console.error('Session check failed:', error);

			// Check if it's a 502 error (bad gateway) which is common with Edge
			if (error.response && error.response.status === 502) {
				console.warn('502 Bad Gateway detected - likely Edge cookie issue, retrying...');
				// Wait a bit and retry once
				setTimeout(() => {
					checkSessionStatus();
				}, 1000);
				return;
			}

			// On other network errors, redirect to login
			sessionStorage.removeItem('authData');
			setIsAuthenticated(false);
			setAccount(null);
			setAccessToken(null);
			window.location.href = '/auth/login';
		}
	};

	// Set up session checking interval
	useEffect(() => {
		// Clear any existing interval
		if (sessionCheckIntervalRef.current) {
			clearInterval(sessionCheckIntervalRef.current);
			sessionCheckIntervalRef.current = null;
		}

		// Only set up interval if authenticated and not bypassing auth
		if (isAuthenticated && !bypassAuth && !isLoading) {
			// console.log('Setting up session check interval (60 seconds)');
			sessionCheckIntervalRef.current = setInterval(checkSessionStatus, 60000);
		}

		// Cleanup interval on unmount or when authentication state changes
		return () => {
			if (sessionCheckIntervalRef.current) {
				clearInterval(sessionCheckIntervalRef.current);
				sessionCheckIntervalRef.current = null;
			}
		};
	}, [isAuthenticated, bypassAuth, isLoading]);

	// Check authentication status immediately on mount
	useEffect(() => {
		// If auth is bypassed, set fake authenticated state and skip all checks
		if (bypassAuth) {
			setIsAuthenticated(true);
			setAccount({ name: 'Test User', username: 'test@example.com' });
			setAccessToken('bypass-token');
			setTokenCache(null);
			setRoles([]);
			setIsLoading(false);
			return;
		}

		const checkAuth = async () => {
			let authData = null;
			
			// First, try to get the token from the backend regardless of the auth flow
			try {
				console.log('Fetching token from backend...');
				const fetchResponse = await fetch('/auth/token', {
					credentials: 'include'
				});
				const data = await safeFetchJson(fetchResponse);
				if (!fetchResponse.ok) {
					const err = new Error(`Token request failed with status ${fetchResponse.status}`);
					err.response = { status: fetchResponse.status, data };
					throw err;
				}
				authData = data;
				
				// Only log token info if we're returning from successful auth

				// if (location.search === '?auth=success') {
				// 	console.log('Received token from backend:', {
				// 		isAuthenticated: authData.isAuthenticated,
				// 		account: authData.account ? {
				// 			name: authData.account.name,
				// 			username: authData.account.username
				// 		} : null,
				// 		// Don't log the full access token for security
				// 		accessToken: authData.accessToken ? 'present' : 'missing',

				// 	});
				// }
			} catch (error) {
				console.error('Failed to get token:', error);
				
				// Clear any stale local auth data regardless of which flow we're in
				sessionStorage.removeItem('authData');

				// If this was after an auth redirect, the backend session may be corrupted.
				// Redirect to /auth/login rather than /auth/signout — the Azure AD SSO session
				// is typically still active so the user will re-authenticate transparently.
				// The backend's /auth/login starts a fresh interactive flow without requiring
				// a full IdP signout.
				if (location.search === '?auth=success') {
					console.log('Token fetch failed after auth redirect - redirecting to login for fresh auth flow');
					window.location.href = '/auth/login';
					return;
				}
				
				// For normal flow, redirect to login
				window.location.href = '/auth/login';
				return;
			}
			
		// Handle auth=success flow
		if (location.search === '?auth=success') {
			if (authData.isAuthenticated) {
				const resolvedRoles = Array.isArray(authData.roles) ? authData.roles : [];

				// Authenticated but no roles — block app access before setting state
				if (resolvedRoles.length === 0 || authData.noRoles) {
					setIsAuthenticated(true);
					setAccount(authData.account);
					setAccessToken(authData.accessToken);
					setTokenCache(authData.tokenCache);
					setRoles([]);
					sessionStorage.setItem('authData', JSON.stringify({
						isAuthenticated: true,
						account: authData.account,
						accessToken: authData.accessToken,
						tokenCache: authData.tokenCache,
						roles: []
					}));
					setIsLoading(false);
					navigate('/no-permission', { replace: true });
					return;
				}

				setIsAuthenticated(true);
				setAccount(authData.account);
				setAccessToken(authData.accessToken);
				setTokenCache(authData.tokenCache);
				setRoles(resolvedRoles);
				sessionStorage.setItem('authData', JSON.stringify({
					isAuthenticated: true,
					account: authData.account,
					accessToken: authData.accessToken,
					tokenCache: authData.tokenCache,
					roles: resolvedRoles
				}));
				
				console.log('Successfully stored auth data and redirecting to home...');
				sessionStorage.removeItem('reauthInProgress');
				navigate('/schedule', { replace: true });
				setIsLoading(false);
				return;
			}
			// If not authenticated after auth=success, redirect to login
			window.location.href = '/auth/login';
			return;
		}

			// Normal auth check flow - we already have authData from the single API call
			const storedAuth = sessionStorage.getItem('authData');
			
			// If we're already on the login page, don't redirect
			if (location.pathname.startsWith('/auth/')) {
				setIsLoading(false);
				return;
			}

			if (!storedAuth) {
				// No stored auth data, redirect to login
				window.location.href = '/auth/login';
				return;
			}

			if (!authData.isAuthenticated) {
				// Token invalid/expired
				sessionStorage.removeItem('authData');
				window.location.href = '/auth/login';
				return;
			}

		// Update auth state with valid data
		const resolvedRoles = Array.isArray(authData.roles) ? authData.roles : [];
		setIsAuthenticated(true);
		setAccount(authData.account);
		setAccessToken(authData.accessToken);
		setTokenCache(authData.tokenCache);
		setRoles(resolvedRoles);
		sessionStorage.setItem('authData', JSON.stringify({
			isAuthenticated: true,
			account: authData.account,
			accessToken: authData.accessToken,
			tokenCache: authData.tokenCache,
			roles: resolvedRoles
		}));

		// No roles assigned — redirect to no-permission before loading completes
		if (resolvedRoles.length === 0 || authData.noRoles) {
			setIsLoading(false);
			navigate('/no-permission', { replace: true });
			return;
		}

		// Successful auth — clear any in-progress reauth guard so future 401s
		// from business API calls can trigger a fresh redirect if needed.
		sessionStorage.removeItem('reauthInProgress');
		setIsLoading(false);
		};

		checkAuth();
	}, [location.pathname, location.search, navigate, bypassAuth]);

	if (isLoading) {
		return null; // Or a loading spinner
	}

	const login = async () => {
		// Redirect to backend auth route
		window.location.href = '/auth/login';
	};

	//
	const logout = () => {
		// Clear the session check interval
		if (sessionCheckIntervalRef.current) {
			clearInterval(sessionCheckIntervalRef.current);
			sessionCheckIntervalRef.current = null;
		}

		// Clear local storage/session data
		sessionStorage.removeItem('authData');
		setIsAuthenticated(false);
		setAccount(null);
		setAccessToken(null);
		setRoles([]);
		
		// Redirect to Microsoft's logout page with post-logout redirect
		const msalConfig = JSON.parse(sessionStorage.getItem('msalConfig') || '{}');
		const authority = msalConfig.auth?.authority || 'https://login.microsoftonline.com/ba85f1ce-efcd-4880-a59b-32da01b93ecd';
		const postLogoutRedirectUri = encodeURIComponent(window.location.origin);
		
		// Create and navigate to the logout URL instead of directly changing location
		// This prevents navigation cancellation that might occur with window.location.href
		const logoutUrl = `${authority}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`;
		
		// Use setTimeout to ensure state updates complete before navigation
		setTimeout(() => {
			// Create a link element and trigger it programmatically
			const logoutLink = document.createElement('a');
			logoutLink.href = logoutUrl;
			logoutLink.style.display = 'none';
			document.body.appendChild(logoutLink);
			logoutLink.click();
			
			// Clean up
			setTimeout(() => {
				document.body.removeChild(logoutLink);
			}, 100);
		}, 0);
	};
	

	const acquireToken = async () => {
		try {
			const fetchResponse = await fetch('/auth/token', {
				credentials: 'include'
			});
			const data = await safeFetchJson(fetchResponse);
			if (!fetchResponse.ok) {
				const err = new Error(`Token request failed with status ${fetchResponse.status}`);
				err.response = { status: fetchResponse.status, data };
				throw err;
			}
			const authData = data;
			
		// Store all necessary data including tokenCache
		const refreshedRoles = Array.isArray(authData.roles) ? authData.roles : [];
		const authDataToStore = {
			isAuthenticated: authData.isAuthenticated,
			account: authData.account,
			accessToken: authData.accessToken,
			tokenCache: authData.tokenCache,
			roles: refreshedRoles
		};
		
		sessionStorage.setItem('authData', JSON.stringify(authDataToStore));
		setIsAuthenticated(true);
		setAccount(authData.account);
		setAccessToken(authData.accessToken);
		setTokenCache(authData.tokenCache);
		setRoles(refreshedRoles);

		// If roles were revoked mid-session, redirect to no-permission
		if (refreshedRoles.length === 0 || authData.noRoles) {
			navigate('/no-permission', { replace: true });
			return null;
		}
		
		return authData.accessToken;
		} catch (error) {
			console.error('[AUTH] acquireToken failed:', error);

			const errorData = error.response?.data || {};
			const reason = errorData.reason || 'UNKNOWN';
			const redirect = errorData.redirect || '/auth/login';

			// Prefer interactive reauth over full IdP logout. The Azure AD SSO session
			// is typically still alive so users re-authenticate transparently via
			// /auth/login. A reauthInProgress flag prevents concurrent failing
			// requests from all triggering simultaneous redirects.
			sessionStorage.removeItem('authData');
			if (!sessionStorage.getItem('reauthInProgress')) {
				sessionStorage.setItem('reauthInProgress', 'true');
				console.warn(`[AUTH] Token acquisition failed (reason=${reason}), redirecting to ${redirect}`);
				window.location.href = redirect;
			}
			return null;
		}
	};

	return (
		<AuthContext.Provider value={{
			isAuthenticated,
			account,
			accessToken,
			tokenCache,
			roles,
			hasRoles: Array.isArray(roles) && roles.length > 0,
			login,
			logout,
			acquireToken
		}}>
			{children}
		</AuthContext.Provider>
	);
};